import { authService } from '@/bundles/Auth/factory/authServiceFactory';
import { MultiAuthReturn } from '@/bundles/Auth/models/MultiAuthReturn';
import { RootMutations } from '@/store/types/mutation-types';
import { AuthReturn } from '@/bundles/Auth/models/AuthReturn';
import { RootActions } from '@/store/types/action-types';
import { accountSettingsService } from '@/bundles/Account/factory/accountSettingsServiceFactory';
import UpdateAccountSettings from '@/bundles/Account/dto/update-account-settings';
import { ServiceFactory } from '@/services/ServiceFactory';
import UserCredentialsDto from '@/bundles/Auth/dto/user-credentials';
import { accountService } from '@/bundles/Account/factory/accountServiceFactory';
import { OtpAuthResponse } from '@/bundles/Auth/models/OtpAuthResponse';
import { StoreModulesNamesEnum } from '@/store/StoreModulesNamesEnum';
import { RESET_ACTIVE_MEMBERS } from '@/store/modules/members/members-types';

import { RESET_SELECTED, RESET_TABLE_OPTIONS } from './modules/tableOptions/types';
import instance from '../services/instance'
import * as constants from './action-types'

// Table options, row selections and the member list belong to one account
const resetAccountScopedState = (commit: (type: string) => void): void => {
  commit(`TableOptions/${RESET_TABLE_OPTIONS}`);
  commit(`TableOptions/${RESET_SELECTED}`);
  commit(`${StoreModulesNamesEnum.Member}/${RESET_ACTIVE_MEMBERS}`);
};

export default {
  [constants.GET_INSTALLATION_INFO] ({ commit }, { id, token }) {
    return new Promise((resolve, reject) => {
      instance
        .get(`/workOrder/install/${id}`, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        })
        .then((response) => {
          const { result } = response.data
          resolve(result)
        })
        .catch((err) => {
          reject(new Error(err))
        })
    })
  },
  [constants.COMPLETE_INSTALLATION] ({ commit }, { id, payload, token }) {
    return new Promise((resolve, reject) => {
      instance
        .put(`/workOrder/install/${id}`, payload, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        })
        .then((response) => {
          resolve(response)
        })
        .catch((err: any) => {
          reject(new Error(err))
        })
    })
  },
  async [constants.LOGIN] ({ commit, dispatch }, payload: UserCredentialsDto) {
    try {
      const response = await authService.signIn(payload);

      if (response instanceof OtpAuthResponse) {
        commit(RootMutations.SET_TEMPORARY_TOKEN, response.access_token);
        return response;
      }

      // awaited so the caller can redirect once the signed-in account is known
      await dispatch(RootActions.SET_LOGIN_INFO, response);
    } catch (error: any) {
      throw new Error(error.message);
    } finally {
      commit(constants.TOGGLE_SPLASH_SCREEN, false);
    }
  },
  async [RootActions.LOG_IN_WITH_MAGIC_LINK] ({ dispatch, commit }, payload: { token: string, code_verifier: string }) {
    try {
      const response = await authService.loginWithMagicLink(payload);

      const mutationName = response instanceof MultiAuthReturn ? RootMutations.SET_TEMPORARY_AUTH_DATA : RootMutations.SET_AUTH_INFO;
      commit(mutationName, response);
    } catch (error: any) {
      throw new Error(error.message);
    }
  },
  async [RootActions.SET_LOGIN_INFO] ({ commit, dispatch }, payload: AuthReturn | MultiAuthReturn) {
    const mutationName = payload instanceof MultiAuthReturn ? RootMutations.SET_TEMPORARY_AUTH_DATA : RootMutations.SET_AUTH_INFO;
    commit(mutationName, payload);
    if (!(payload instanceof MultiAuthReturn)) {
      await dispatch(RootActions.FETCH_CURRENT_USER);
    }
  },
  async [RootActions.MULTI_ACCOUNT_SIGN_IN] ({ commit }, payload: { account: number }) {
    const response = await authService.multiAccountSignIn(payload);
    commit(RootMutations.SET_AUTH_INFO, response);
  },
  async [RootActions.FETCH_CURRENT_USER] ({ commit, state }) {
    const response = await authService.me();
    if (state?.activeAccount?.id !== response?.active_account?.id) {
      resetAccountScopedState(commit);
    }
    commit(RootMutations.SET_CURRENT_USER, response);
  },
  // After an account switch, before the reload: the app reloads anyway and its boot asks /auth/me, so the
  // account is taken from the user's account list instead of a second /auth/me here (~0.8 s on staging).
  [RootActions.ACTIVATE_ACCOUNT] ({ commit, state }, account: { id: number; name: string }) {
    if (state?.activeAccount?.id !== account.id) {
      resetAccountScopedState(commit);
    }
    commit(RootMutations.SET_ACTIVE_ACCOUNT, account);
  },
  async [RootActions.FETCH_ACCOUNT_SETTINGS] ({ commit, state }) {
    const response = await accountSettingsService.getOne(state.activeAccount.id);
    commit(RootMutations.SET_ACCOUNT_SETTING, response);
  },
  async [RootActions.FETCH_ACCOUNT_PREFERENCES] ({ commit }) {
    const response = await accountService.getPreferences();
    commit(RootMutations.SET_ACCOUNT_PREFERENCES, response);
  },
  async [RootActions.UPDATE_ACCOUNT_SETTINGS] ({ commit, state }, payload: UpdateAccountSettings) {
    const response = await accountSettingsService.updateOne(state.activeAccount.id, payload);
    commit(RootMutations.SET_ACCOUNT_SETTING, response);
  },
  async [RootActions.FETCH_USER_INTEGRATIONS] ({ commit, state }) {
    const integrationsInstance = ServiceFactory.get('integrations');
    const { data } = await integrationsInstance.getMyIntegrations({ token: state.idToken });
    commit(RootMutations.SET_USER_INTEGRATIONS, data);
  },
  [RootActions.CANCEL_PENDING_REQUESTS] ({ state, commit }) {
    for (const token of state.cancelTokens) {
      if (token?.persistOnRouteChange) { // skip tokens marked as persistent
        continue;
      }

      if (token?.controller?.abort) {
        token.controller.abort();
      }
    }

    commit(RootMutations.CLEAR_CANCEL_TOKENS, true); // keep only persistent tokens
  }
}
